import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import toast from 'components/partials/toast/toast';

import { updateConversation } from 'api/generated/ai-assistant-controller/ai-assistant-controller';
import type { ConversationUpdateResponse } from 'api/generated/schemas/conversationUpdateResponse';

import { AI_ASSISTANT_CONVERSATIONS_KEY } from '../ai-assistant.constants';

interface UseApiRenameConversationArgs {
  oktaUserId: string;
  clientId: string | number;
}

interface RenameVariables {
  conversationId: string;
  name: string;
}

interface ApiErrorLike {
  data?: { detail?: string };
}

export const useApiRenameConversation = ({
  oktaUserId,
  clientId,
}: UseApiRenameConversationArgs): UseMutationResult<
  ConversationUpdateResponse,
  ApiErrorLike,
  RenameVariables
> => {
  const queryClient = useQueryClient();

  return useMutation<ConversationUpdateResponse, ApiErrorLike, RenameVariables>(
    {
      mutationFn: ({ conversationId, name }) =>
        updateConversation(
          oktaUserId,
          conversationId,
          { name },
          clientId
            ? { headers: { 'X-Client-Id': String(clientId) } }
            : undefined
        ),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [AI_ASSISTANT_CONVERSATIONS_KEY, oktaUserId, clientId],
        });
      },
      onError: () =>
        toast.error({
          title: 'Unable to rename chat',
          message:
            'Something went wrong while renaming this chat. Please try again.',
        }),
    }
  );
};
